import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ensurePushSubscription, unsubscribePush } from "@/lib/push";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const LS_EMPRESA = "current_empresa_id";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [currentEmpresaId, setCurrentEmpresaId] = useState(localStorage.getItem(LS_EMPRESA) || "");
  const [loading, setLoading] = useState(true);
  // Branding do tenant — carregado por <TenantBrandingSync/> reagindo à mudança de rota.
  const [tenantBranding, setTenantBranding] = useState(null);

  const loadEmpresas = async () => {
    try {
      const r = await api.get("/empresas");
      setEmpresas(r.data || []);
      const stored = localStorage.getItem(LS_EMPRESA);
      const exists = (r.data || []).some((e) => e.id === stored);
      if (!exists && r.data?.length) {
        localStorage.setItem(LS_EMPRESA, r.data[0].id);
        setCurrentEmpresaId(r.data[0].id);
      }
      return r.data || [];
    } catch { return []; }
  };

  useEffect(() => {
    // Auth via cookie httpOnly — apenas consulta /auth/me. Se cookie inválido/ausente, 401 → deslogado.
    api.get("/auth/me")
      .then(async (r) => {
        setUser(r.data);
        if (!r.data.entregador_id) await loadEmpresas();
        // silencioso: registra push se já autorizado
        ensurePushSubscription().catch(() => { /* push silencioso — falha não afeta login */ });
      })
      .catch(() => { /* não autenticado — segue para login */ })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, senha) => {
    const r = await api.post("/auth/login", { email, senha });
    // 2FA (fev/2026): SA com 2FA habilitado — backend enviou pre_auth cookie
    // e sinalizou requires_2fa. Não seta user ainda; caller precisa chamar verify2FA.
    if (r.data?.requires_2fa) {
      return { requires_2fa: true, user: null, empresas: [], redirect_slug: null };
    }
    // Cookie httpOnly `access_token` foi setado pelo backend automaticamente
    setUser(r.data.user);
    const emps = r.data.empresas || [];
    setEmpresas(emps);
    if (emps.length) {
      const stored = localStorage.getItem(LS_EMPRESA);
      const exists = emps.some((e) => e.id === stored);
      const chosen = exists ? stored : emps[0].id;
      localStorage.setItem(LS_EMPRESA, chosen);
      setCurrentEmpresaId(chosen);
    }
    // Ativa push (pede permissão) após login bem-sucedido
    ensurePushSubscription().catch(() => { /* push silencioso — falha não bloqueia login */ });
    return { user: r.data.user, empresas: emps, redirect_slug: r.data.redirect_slug };
  };

  const verify2FA = async (code) => {
    // Completa o login após desafio 2FA (SA). Backend seta o auth cookie definitivo.
    const r = await api.post("/auth/2fa/verify", { code });
    setUser(r.data.user);
    const emps = r.data.empresas || [];
    setEmpresas(emps);
    if (emps.length) {
      const stored = localStorage.getItem(LS_EMPRESA);
      const exists = emps.some((e) => e.id === stored);
      const chosen = exists ? stored : emps[0].id;
      localStorage.setItem(LS_EMPRESA, chosen);
      setCurrentEmpresaId(chosen);
    }
    ensurePushSubscription().catch(() => { /* silencioso */ });
    return { user: r.data.user, empresas: emps, redirect_slug: r.data.redirect_slug };
  };

  const logout = async () => {
    unsubscribePush().catch(() => { /* unsub silencioso — se falhar cookie é limpo assim mesmo */ });
    try { await api.post("/auth/logout"); } catch { /* backend pode estar offline — segue */ }
    localStorage.removeItem("token"); // backward-compat: limpa token legado do localStorage
    localStorage.removeItem(LS_EMPRESA);
    setUser(null); setEmpresas([]); setCurrentEmpresaId("");
  };

  const switchEmpresa = (id) => {
    localStorage.setItem(LS_EMPRESA, id);
    setCurrentEmpresaId(id);
    window.dispatchEvent(new Event("empresa-changed"));
  };

  // ---- Impersonate (Modo Suporte) ----
  const startImpersonate = async (empresaId) => {
    const r = await api.post(`/auth/impersonate/${empresaId}`);
    // Cookie foi trocado pelo backend — recarrega /auth/me para pegar os claims novos
    const me = await api.get("/auth/me");
    setUser(me.data);
    const emps = await loadEmpresas();
    // Força seleção da empresa impersonada
    if (emps.length) { localStorage.setItem(LS_EMPRESA, empresaId); setCurrentEmpresaId(empresaId); }
    window.dispatchEvent(new Event("empresa-changed"));
    return r.data;
  };

  const endImpersonate = async () => {
    await api.post("/auth/end-impersonate");
    // Cookie do super_admin restaurado — recarrega /auth/me
    const me = await api.get("/auth/me");
    setUser(me.data);
    await loadEmpresas();
    window.dispatchEvent(new Event("empresa-changed"));
  };

  const refreshEmpresas = useCallback(() => loadEmpresas(), []);

  const currentEmpresa = useMemo(
    () => empresas.find((e) => e.id === currentEmpresaId) || null,
    [empresas, currentEmpresaId]
  );

  const ctxValue = useMemo(
    () => ({
      user, loading, login, logout, verify2FA, empresas, currentEmpresa, currentEmpresaId,
      switchEmpresa, refreshEmpresas,
      tenantBranding, setTenantBranding,
      startImpersonate, endImpersonate,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, empresas, currentEmpresa, currentEmpresaId, refreshEmpresas, tenantBranding]
  );

  return (
    <AuthCtx.Provider value={ctxValue}>
      {children}
    </AuthCtx.Provider>
  );
}
